import { useEffect, useState } from "react";
import { teamApi } from "../../services/teamApi";
import { useToast } from "../../context/ToastContext";
import { useTeamRole } from "../../hooks/useTeamRole";
import { Card } from "../../components/common/Card";
import { PageHeader } from "../../components/common/PageHeader";
import { Button } from "../../components/common/Button";
import { Badge } from "../../components/common/Badge";
import { DataTable } from "../../components/common/DataTable";
import { RankedTaskList } from "../../components/tasks/RankedTaskList";
import { hivesApi } from "../../services/hivesApi";
import { Modal } from "../../components/common/Modal";
import { formatDateOnly } from "../../services/hiveActions";
import { SelectOrAdd } from "../../components/common/SelectOrAdd";
import { Field } from "../../components/common/Field";
import { ConfirmDialog } from "../../components/common/ConfirmDialog";

/*
 * What a person can SET a task to.
 *
 * Overdue is deliberately not here. It is derived -- the row already
 * works it out from due_date and shows "3 days overdue" -- so offering it
 * as a choice let the two disagree: a task could be marked Overdue while
 * its date said otherwise, or sit at Pending while three days late. A
 * task that already carries the status still renders it (RankedTaskList
 * prepends any unlisted current status), it just cannot be chosen.
 */
const STATUSES = ["Pending", "InProgress", "Completed"];
const STATUS_TONE = { Pending: "neutral", InProgress: "info", Completed: "success", Overdue: "danger" };

/*
 * Leaving a task for whoever gets there (#394, V101).
 *
 * A real answer rather than a blank: unclaimed work is work anyone can
 * pick up, and the way it sits undone is by being invisible to everyone
 * until somebody is named. A sentinel rather than "" because the select
 * stores strings and an empty string is the untouched field.
 */
const ANYONE = "__anyone__";

/* Whose tasks the list is showing (#394). */
const MINE = "mine";
const EVERYONE = "all";
const BY_MEMBER = "member";

const EMPTY_FORM = {
  assigned_to_user_id: "", task_type: "", due_date: "", priority: "Medium",
  description: "", recurs_every_days: "", hive_id: "", site_id: "",
};

export function TeamTasksPage() {
  const { notify } = useToast();
  const { canCreate, canManage } = useTeamRole();
  const [tasks, setTasks] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  // "when" answers what to do next; "site" answers what to do once you
  // have driven there. Different questions, so both rather than one
  // compromise between them (#283).
  const [taskView, setTaskView] = useState("when");
  /*
   * What to show. "open" by default, because a task list is a list of
   * work outstanding -- completed tasks accumulated here forever and
   * pushed the ones that still need doing down the page.
   *
   * Client-side over the tasks already loaded rather than a request per
   * change: the grouping and the ranking are computed here anyway, so a
   * round trip would buy nothing and make the filter feel slow.
   */
  const [statusFilter, setStatusFilter] = useState("open");
  /*
   * WHOSE tasks, which is a different question from which STATUS (#394).
   *
   * "Mine" leads, because somebody opening this page is usually asking
   * what they have to do -- and it means mine OR nobody's, which is the
   * definition the report gave and the reason it matters: unclaimed work
   * is work anyone can pick up, and hiding it behind "All" is how it
   * sits undone.
   */
  const [whoFilter, setWhoFilter] = useState(MINE);
  const [whoMember, setWhoMember] = useState("");
  // The task awaiting delete confirmation, or null.
  const [pendingDelete, setPendingDelete] = useState(null);
  // Only for grouping by yard. Best-effort and never blocking: the task
  // list must render whether or not the hive list arrives.
  const [hives, setHives] = useState([]);
  const [taskIssues, setTaskIssues] = useState({});
  const [members, setMembers] = useState([]);
  const [handoffOpen, setHandoffOpen] = useState(false);
  const [handoffForm, setHandoffForm] = useState({ inspection_id: "", to_user_id: "", notes: "" });
  // Inspections this person could hand off, or null while unknown. Null
  // rather than [] so "loading" and "you have none" can say different
  // things -- an empty picker that means the former reads as the latter.
  const [inspections, setInspections] = useState(null);

  const [templates, setTemplates] = useState(null);
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [templateForm, setTemplateForm] = useState({ name: "", task_type: "", default_priority: "Medium", default_description: "" });

  const [handoffs, setHandoffs] = useState(null);
  const [completingHandoff, setCompletingHandoff] = useState(null);
  const [completeNotes, setCompleteNotes] = useState("");

  async function load() {
    const r = await teamApi.listTasks();
    setTasks(r.tasks);
  }
  async function loadTemplates() {
    const r = await teamApi.listTemplates().catch(() => ({ templates: [] }));
    setTemplates(r.templates);
  }
  async function loadHandoffs() {
    const r = await teamApi.listHandoffs(false).catch(() => ({ handoffs: [] }));
    setHandoffs(r.handoffs);
  }
  async function loadMembers() {
    // Empty on failure rather than throwing: the rest of the page is still
    // usable, and the picker says so itself.
    const r = await teamApi.listMembers().catch(() => ({ members: [] }));
    setMembers(r.members || []);
  }
  useEffect(() => {
    let cancelled = false;
    hivesApi.list()
      .then((r) => !cancelled && setHives(r.hives || []))
      .catch(() => {});
    // A hive's open issue is what makes the same task rank differently on
    // two different hives -- the thing a flat list cannot do.
    hivesApi.openIssues?.()
      .then((r) => {
        if (cancelled) return;
        const map = {};
        for (const issue of r.issues || []) {
          const level = issue.severity === "Urgent" ? "urgent" : "watch";
          if (map[issue.hive_id] !== "urgent") map[issue.hive_id] = level;
        }
        setTaskIssues(map);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  useEffect(() => { load(); loadTemplates(); loadHandoffs(); loadMembers(); }, []);

  function applyTemplate(t) {
    setForm((f) => ({ ...f, task_type: t.task_type, priority: t.default_priority, description: t.default_description || "" }));
    setOpen(true);
  }

  async function createTemplate() {
    if (!templateForm.name.trim() || !templateForm.task_type.trim()) return notify("Name and task type are required", "warning");
    try {
      await teamApi.createTemplate(templateForm);
      notify("Template saved", "success");
      setTemplateForm({ name: "", task_type: "", default_priority: "Medium", default_description: "" });
      loadTemplates();
    } catch (err) {
      notify(err.apiMessage || "Could not save template", "danger");
    }
  }

  async function removeTemplate(templateId) {
    try {
      await teamApi.deleteTemplate(templateId);
      loadTemplates();
    } catch (err) {
      notify(err.apiMessage || "Could not remove template", "danger");
    }
  }

  async function createTask() {
    if (!form.assigned_to_user_id || !form.task_type) return notify("Assignee and task type are required", "warning");
    try {
      await teamApi.createTask({
        // ANYONE is a chosen answer and sends null; "" is the untouched
        // field and is refused above.
        assigned_to_user_id: form.assigned_to_user_id === ANYONE
          ? null
          : Number(form.assigned_to_user_id),
        task_type: form.task_type,
        due_date: form.due_date || undefined,
        priority: form.priority,
        // Which hive it is about (#288). app.TeamTasks has had hive_id
        // since V011 and the form never offered it, so every task was
        // account-level -- and the by-site view in #283 had nothing to
        // group, because a task with no hive belongs to no yard.
        hive_id: form.hive_id ? Number(form.hive_id) : undefined,
        /* WHERE, when the answer is a yard rather than one colony
           (#393). "Inspect the hives at Site 7" is a normal instruction,
           and it is NOT the same as a task with no location -- so the
           two do not collapse into one field. Storing it as "every hive
           at the site" would be wrong: the hives at a site change, and a
           task recorded against a list of them would not follow. */
        site_id: form.site_id ? Number(form.site_id) : undefined,
        description: form.description || undefined,
        recurs_every_days: form.recurs_every_days ? Number(form.recurs_every_days) : undefined,
      });
      notify("Task assigned", "success");
      setOpen(false);
      setForm(EMPTY_FORM);
      load();
    } catch (err) {
      notify(err.apiMessage || "Could not assign task", "danger");
    }
  }

  async function updateStatus(taskId, status) {
    try {
      await teamApi.updateTask(taskId, status);
      load();
    } catch (err) {
      notify(err.apiMessage || "Could not update task", "danger");
    }
  }

  async function confirmDeleteTask() {
    const task = pendingDelete;
    if (!task) return;
    try {
      await teamApi.deleteTask(task.task_id);
      setPendingDelete(null);
      notify("Task deleted", "success");
      load();
    } catch (err) {
      notify(err.apiMessage || "Could not delete task", "danger");
    }
  }

  useEffect(() => {
    if (!handoffOpen) return undefined;
    let cancelled = false;
    teamApi.handoffCandidates()
      .then((r) => !cancelled && setInspections(r.inspections || []))
      // An empty list here would claim you have nothing to hand off,
      // which is a different statement from "we could not ask".
      .catch(() => !cancelled && setInspections([]))
      .finally(() => undefined);
    return () => { cancelled = true; };
  }, [handoffOpen]);

  async function submitHandoff() {
    if (!handoffForm.inspection_id || !handoffForm.to_user_id) return notify("Pick an inspection and who is taking it on", "warning");
    try {
      await teamApi.handoff({ inspection_id: Number(handoffForm.inspection_id), to_user_id: Number(handoffForm.to_user_id), notes: handoffForm.notes || undefined });
      notify("Handoff started", "success");
      setHandoffOpen(false);
      setHandoffForm({ inspection_id: "", to_user_id: "", notes: "" });
      loadHandoffs();
    } catch (err) {
      notify(err.apiMessage || "Could not start handoff", "danger");
    }
  }

  /*
   * Which of two colliding handoffs is right (#368).
   *
   * Two people out of signal can hand the same inspection to different
   * teammates; the server marks both contested rather than letting the
   * later sync silently win. Web cannot CREATE that collision -- it has
   * no offline queue -- but the desk is where somebody notices it, and a
   * contest only the phone could settle would sit there unanswered.
   */
  async function settleHandoff(handoffId) {
    try {
      await teamApi.settleHandoff(handoffId);
      notify("Settled. The other handoff was cancelled and stays on the record.", "success");
      loadHandoffs();
    } catch (err) {
      notify(err.apiMessage || "Could not settle that handoff", "danger");
    }
  }

  function openCompleteHandoff(h) {
    setCompletingHandoff(h);
    setCompleteNotes("");
  }

  async function submitCompleteHandoff() {
    try {
      await teamApi.completeHandoff(completingHandoff.handoff_id, completeNotes || undefined);
      notify("Handoff marked complete", "success");
      setCompletingHandoff(null);
      loadHandoffs();
    } catch (err) {
      notify(err.apiMessage || "Could not complete handoff", "danger");
    }
  }

  // Counts come from ALL tasks, not the filtered set, so the option
  // labels can say what switching to them would show.
  const doneCount = (tasks || []).filter((t) => t.status === "Completed").length;
  const openCount = (tasks || []).length - doneCount;
  /*
   * WHOSE, decided by the server (#394).
   *
   * is_mine and is_unassigned come from /team/tasks rather than being
   * worked out here: the server already knows who is asking, and this
   * codebase refuses to ship the caller's user_id to a client that would
   * then have to be told who it is -- the same rule the hive list
   * follows for is_my_account.
   *
   * A row carrying NEITHER flag is SHOWN. An older API, or a response
   * cached before this shipped, cannot be filtered on -- and hiding work
   * because a field is absent is how a task never gets done.
   */
  const matchesWho = (t) => {
    if (whoFilter === EVERYONE) return true;
    if (whoFilter === BY_MEMBER) {
      return !whoMember || String(t.assigned_to_user_id) === String(whoMember);
    }
    if (t.is_mine === undefined && t.is_unassigned === undefined) return true;
    return Boolean(t.is_mine) || Boolean(t.is_unassigned);
  };

  const shownTasks = !tasks ? tasks : tasks.filter((t) => (
    statusFilter === "all" ? true
      : statusFilter === "done" ? t.status === "Completed"
        : t.status !== "Completed"
  )).filter(matchesWho);

  /*
   * The hives this task could be about, narrowed by the chosen site
   * (#393). Leaving the whole list would make choosing a site cost a
   * click and save none.
   */
  const hivesForForm = form.site_id
    ? hives.filter((h) => String(h.site_id) === String(form.site_id))
    : hives;

  /*
   * The yards, derived from the hive list already loaded rather than
   * fetched again. A site with no hives cannot be the subject of
   * "inspect the hives at Site 7", so deriving loses nothing here and
   * saves a request on a page that already makes several.
   */
  const sitesForForm = [...new Map(
    hives.filter((h) => h.site_id != null)
      .map((h) => [String(h.site_id), { site_id: h.site_id, site_name: h.site_name || "Unnamed site" }]),
  ).values()].sort((a, b) => a.site_name.localeCompare(b.site_name, undefined, { sensitivity: "base" }));

  return (
    <div>
      <PageHeader title="Team Tasks">
        <Button variant="secondary" onClick={() => setTemplatesOpen(true)}>Task Templates</Button>
        {canCreate && <Button variant="secondary" onClick={() => setHandoffOpen(true)}>Inspection Handoff</Button>}
        {/* Assigning is why people open this page, so it goes last: the
            rightmost control on the row. */}
        {canCreate && <Button onClick={() => setOpen(true)}>Assign Task</Button>}
      </PageHeader>

      {!canCreate && (
        <p style={{ color: "var(--color-text-muted)", fontSize: "var(--font-size-sm)" }} className="section-spacing">
          Your Viewer role is read-only -- assigning tasks and starting handoffs is disabled.
        </p>
      )}

      {templates && templates.length > 0 && canCreate && (
        <Card title="Quick-Assign from Template" className="section-spacing">
          <div style={{ display: "flex", gap: "var(--space-2)", flexWrap: "wrap" }}>
            {templates.map((t) => (
              <Button key={t.template_id} size="sm" variant="secondary" onClick={() => applyTemplate(t)}>{t.name}</Button>
            ))}
          </div>
        </Card>
      )}

      {/* section-spacing is margin-BOTTOM only, so a card without it sits
          flush against whatever follows. Tasks ran straight into Pending
          Handoffs with no gap between them. */}
      <Card
        title="Tasks"
        className="section-spacing"
        actions={
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {/* A SELECT, not three more buttons. Filtering and grouping
                are different questions, and five same-sized buttons in
                one header is the row docs/UI_ACTIONS.md rule 6 exists to
                prevent. The counts are in the labels so the cost of
                switching is visible before the click. */}
            <select id="team-tasks-page-status-filter" name="status-filter"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="touch-target"
              aria-label="Which tasks to show"
            >
              <option value="open">To do ({openCount})</option>
              <option value="all">All tasks ({(tasks || []).length})</option>
              <option value="done">Completed ({doneCount})</option>
            </select>
            {/* WHOSE, which is a different question from which status
                (#394) -- so a second select rather than six combined
                options in the first, which would multiply as either list
                grew. */}
            <select id="team-tasks-page-who-filter" name="who-filter"
              value={whoFilter}
              onChange={(e) => {
                setWhoFilter(e.target.value);
                // Cleared on the way out, so returning to By member does
                // not silently re-apply whoever was chosen last time.
                if (e.target.value !== BY_MEMBER) setWhoMember("");
              }}
              className="touch-target"
              aria-label="Whose tasks to show"
            >
              <option value={MINE}>Mine and unclaimed</option>
              <option value={EVERYONE}>Everyone's</option>
              <option value={BY_MEMBER}>One team member...</option>
            </select>
            {whoFilter === BY_MEMBER ? (
              <select id="team-tasks-page-who-member" name="who-member"
                value={whoMember}
                onChange={(e) => setWhoMember(e.target.value)}
                className="touch-target"
                aria-label="Whose tasks"
              >
                {/* Because somebody is on holiday and their work still
                    has to happen -- the reason that was given, and a
                    better one than tidiness. */}
                <option value="">Anyone on the team</option>
                {members.map((m) => (
                  <option key={m.user_id} value={m.user_id}>{m.email}</option>
                ))}
              </select>
            ) : null}
            <Button size="sm" variant={taskView === "when" ? "primary" : "secondary"} onClick={() => setTaskView("when")}>By when</Button>
            <Button size="sm" variant={taskView === "site" ? "primary" : "secondary"} onClick={() => setTaskView("site")}>By site</Button>
          </div>
        }
      >
        <RankedTaskList
          {...(statusFilter === "open" && doneCount > 0
            ? { emptyTitle: "Nothing left to do",
                emptyDescription: `All ${doneCount} ${doneCount === 1 ? "task is" : "tasks are"} completed. Switch to All tasks to see them.` }
            : statusFilter === "done"
              ? { emptyTitle: "Nothing completed yet",
                  emptyDescription: "Tasks marked Completed will collect here." }
              : {})}
          tasks={shownTasks}
          hives={hives}
          issuesByHive={taskIssues}
          view={taskView}
          statuses={STATUSES}
          statusTone={STATUS_TONE}
          onStatus={updateStatus}
          onDelete={setPendingDelete}
          canManage={canManage}
        />
      </Card>

      <Card title="Pending Handoffs" className="section-spacing">
        <DataTable
          rows={handoffs}
          loading={!handoffs}
          rowKey="handoff_id"
          emptyTitle="No pending handoffs"
          emptyDescription="Inspection handoffs waiting to be marked complete will show up here."
          columns={[
            // Names, not user ids. This table rendered "7 -> 9" -- the
            // same primary-key-on-screen problem the harvest form had.
            // The id remains as the fallback rather than a blank cell:
            // an unnameable teammate (an account the caller cannot claim
            // a roster for) should still show SOMETHING identifying.
            {
              key: "inspection_id",
              header: "Inspection",
              // Marked on the row rather than in a banner above the
              // table: the question is WHICH of these two, and an alert
              // that names an inspection id sends the reader hunting for
              // the rows it means.
              render: (r) => (r.contested_at
                ? <>{r.inspection_id} <Badge tone="warning">Two people asked</Badge></>
                : r.inspection_id),
            },
            { key: "from_user_id", header: "From", render: (r) => r.from_name || `#${r.from_user_id}` },
            { key: "to_user_id", header: "To", render: (r) => r.to_name || `#${r.to_user_id}` },
            { key: "handoff_time", header: "Handed Off", render: (r) => new Date(r.handoff_time).toLocaleString() },
            { key: "notes", header: "Notes", render: (r) => r.notes || "—" },
            {
              key: "actions",
              header: "",
              // Secondary (#327). A primary repeated on every row of a
              // table competes with the page's one primary -- Assign Task
              // -- and on a busy handoff list turns the column into a
              // wall of blue that says everything is equally urgent. Row
              // actions are secondary everywhere else.
              render: (r) => (
                <>
                {r.contested_at ? (
                  <>
                    <Button size="sm" variant="secondary" onClick={() => settleHandoff(r.handoff_id)}>
                      This one is right
                    </Button>
                    {" "}
                  </>
                ) : null}
                <Button size="sm" variant="secondary" onClick={() => openCompleteHandoff(r)}>Complete</Button>
                </>
              ),
            },
          ]}
        />
      </Card>

      <Modal open={open} onClose={() => setOpen(false)} title="Assign Task" footer={<Button onClick={createTask}>Assign</Button>}>
        <div style={{ display: "grid", gap: "var(--space-3)" }}>
          {/* Was "Assignee user ID" -- a free-text box expecting a number
              the user had to know from memory. Nobody knows their
              teammate's database id. */}
          <label style={{ display: "grid", gap: 4 }}>
            <span>Assign to</span>
            <select id="team-tasks-page-assigned-to-user-id" name="assigned-to-user-id"
              value={form.assigned_to_user_id}
              onChange={(e) => setForm((f) => ({ ...f, assigned_to_user_id: e.target.value }))}
              className="touch-target"
            >
              <option value="">Select a team member...</option>
              {/* NOT offered to an empty roster: "anyone" with nobody on
                  the team means nobody, and the message below is the
                  useful thing to say in that case. */}
              {members.length > 0 ? (
                <option value={ANYONE}>Anyone — first to get there</option>
              ) : null}
              {members.map((m) => (
                <option key={m.user_id} value={m.user_id}>{m.email}{m.role ? ` (${m.role})` : ""}</option>
              ))}
            </select>
            {members.length === 0 ? (
              <span style={{ color: "var(--color-text-muted)", fontSize: "var(--font-size-sm)" }}>
                No team members yet — invite someone from the Team page first.
              </span>
            ) : null}
          </label>
          {/* Which hive, if any (#288). Optional on purpose: ordering
              jars and paying a supplier are real tasks that belong to
              nobody's hive, and forcing a choice would have people pick
              an arbitrary one to get past the form.

              Grouped by site, because "Hive 3" means nothing on its own
              once there are two yards -- the same reason the hive detail
              page leads with the site name. */}
          {/* WHICH YARD (#393). "Inspect the hives at Site 7" is a normal
              instruction and was the first thing asked for -- and it is
              not the same as a task with no location, so this is its own
              field rather than something inferred from an empty hive. */}
          <label style={{ display: "grid", gap: 4 }}>
            <span>At which site? (optional)</span>
            <select id="team-tasks-page-site-id" name="site-id"
              value={form.site_id}
              onChange={(e) => setForm((f) => (
                // A new site invalidates a hive chosen under the old one.
                // Silently sending a hive from another yard is worse than
                // asking again.
                { ...f, site_id: e.target.value, hive_id: "" }
              ))}
              className="touch-target"
            >
              <option value="">Not about a particular site</option>
              {sitesForForm.map((site) => (
                <option key={site.site_id} value={site.site_id}>{site.site_name}</option>
              ))}
            </select>
          </label>
          <label style={{ display: "grid", gap: 4 }}>
            <span>About which hive? (optional)</span>
            <select id="team-tasks-page-hive-id" name="hive-id"
              value={form.hive_id}
              onChange={(e) => setForm((f) => ({ ...f, hive_id: e.target.value }))}
              className="touch-target"
            >
              <option value="">
                {form.site_id ? "The whole site" : "Not about a particular hive"}
              </option>
              {Object.entries(
                hivesForForm.reduce((acc, h) => {
                  const site = h.site_name || "Unassigned";
                  (acc[site] = acc[site] || []).push(h);
                  return acc;
                }, {}),
              ).map(([site, group]) => (
                <optgroup key={site} label={site}>
                  {group.map((h) => (
                    <option key={h.hive_id} value={h.hive_id}>Hive {h.hive_number}</option>
                  ))}
                </optgroup>
              ))}
            </select>
            {form.site_id && !form.hive_id ? (
              /* "The whole site" and "I forgot to pick a hive" look
                 identical on a form with one field filled and one empty. */
              <span style={{ color: "var(--color-text-muted)", fontSize: "var(--font-size-sm)" }}>
                This task is about the whole site. Pick a hive to narrow it to one.
              </span>
            ) : null}
          </label>
          <SelectOrAdd
            listKey="task_type"
            label="Task type"
            value={form.task_type}
            onChange={(v) => setForm((f) => ({ ...f, task_type: v }))}
          />
          <label style={{ display: "grid", gap: 4 }}>
            <span>Due date</span>
            <input id="team-tasks-page-due-date" name="due-date" type="date" value={form.due_date} onChange={(e) => setForm((f) => ({ ...f, due_date: e.target.value }))} className="touch-target" />
          </label>
          <select id="team-tasks-page-priority" name="priority" value={form.priority} onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value }))} className="touch-target">
            {["Low", "Medium", "High"].map((p) => <option key={p}>{p}</option>)}
          </select>
          <textarea id="team-tasks-page-description" name="description" placeholder="Description (optional)" rows={2} value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
          {/* Recurrence repeats from the COMPLETION, not on a calendar --
              "every 2 weeks" in a yard means two weeks after you last did
              it. See V036 for why a fixed schedule is the wrong model
              here: it piles up overdue work nobody could have done. */}
          {/* Touch target on the label, not the box -- .touch-target forces
              a 48px minimum, which drew a big sharp square unlike every
              other checkbox in the app. Same fix as the site picker on the
              team invite. */}
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: "var(--space-2)",
              minHeight: "var(--touch-target-min)",
              cursor: "pointer",
            }}
          >
            <input
              id="team-tasks-recurs"
              name="recurs"
              type="checkbox"
              checked={form.recurs_every_days !== ""}
              onChange={(e) => setForm((f) => ({ ...f, recurs_every_days: e.target.checked ? "14" : "" }))}
            />
            <span>Repeat this task</span>
          </label>
          {form.recurs_every_days !== "" ? (
            <label style={{ display: "grid", gap: 4 }}>
              <span>Repeat how often?</span>
              <select id="team-tasks-page-recurs-every-days" name="recurs-every-days"
                value={form.recurs_every_days}
                onChange={(e) => setForm((f) => ({ ...f, recurs_every_days: e.target.value }))}
                className="touch-target"
              >
                {[["7", "Weekly"], ["10", "Every 10 days"], ["14", "Every 2 weeks"], ["21", "Every 3 weeks"], ["30", "Monthly"]]
                  .map(([days, label]) => <option key={days} value={days}>{label}</option>)}
              </select>
              <span style={{ color: "var(--color-text-muted)", fontSize: "var(--font-size-sm)" }}>
                The next one is created when this is marked complete, counted from that day.
              </span>
            </label>
          ) : null}
        </div>
      </Modal>

      <Modal open={handoffOpen} onClose={() => setHandoffOpen(false)} title="Inspection Handoff" footer={<Button onClick={submitHandoff}>Start Handoff</Button>}>
        <div style={{ display: "grid", gap: "var(--space-3)" }}>
          {/* Still a typed id, and knowingly so: handing off an inspection
              needs a list of that hive's recent inspections, which no
              endpoint returns today. Labelled and explained rather than
              left as a bare box; the picker is the remaining half of #211. */}
          {/* Was a text box asking for an "Inspection ID" -- app.HiveActions'
              action_id, a BIGINT that appears nowhere in the UI and which
              nobody could know. The hint said a picker was still to come;
              this is it (#211's argument, reported again from dev).

              Your own recent inspections, not the account's: a handoff is
              "I looked at this hive, now you finish it", and the ones
              already handed off are left out rather than greyed, since
              the server refuses them anyway. */}
          <Field
            label="Inspection"
            hint={inspections === null
              ? undefined
              : inspections.length === 0
                ? "No inspections of yours in the last 30 days to hand off."
                : undefined}
          >
            {(id, aria) => (
              <select id={id} {...aria} className="touch-target"
                value={handoffForm.inspection_id}
                disabled={inspections === null || inspections.length === 0}
                onChange={(e) => setHandoffForm((f) => ({ ...f, inspection_id: e.target.value }))}>
                <option value="">
                  {inspections === null ? "Loading…" : "Select an inspection…"}
                </option>
                {(inspections || []).map((i) => (
                  <option key={i.inspection_id} value={i.inspection_id}>
                    {`${i.site_name ? `${i.site_name} — ` : ""}Hive ${i.hive_number} · ${formatDateOnly(i.action_date)}`}
                  </option>
                ))}
              </select>
            )}
          </Field>
          <Field label="Hand off to">
            {(id, aria) => (
              <select
                id={id}
                {...aria}
                value={handoffForm.to_user_id}
                onChange={(e) => setHandoffForm((f) => ({ ...f, to_user_id: e.target.value }))}
                className="touch-target"
              >
                <option value="">Select a team member...</option>
                {members.map((m) => (
                  <option key={m.user_id} value={m.user_id}>{m.email}{m.role ? ` (${m.role})` : ""}</option>
                ))}
              </select>
            )}
          </Field>
          <Field label="Notes">
            {(id, aria) => <textarea id={id} {...aria} rows={3} value={handoffForm.notes} onChange={(e) => setHandoffForm((f) => ({ ...f, notes: e.target.value }))} />}
          </Field>
        </div>
      </Modal>

      <Modal open={templatesOpen} onClose={() => setTemplatesOpen(false)} title="Task Templates">
        <div style={{ display: "grid", gap: "var(--space-3)" }}>
          {(templates || []).map((t) => (
            <div key={t.template_id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid var(--color-border)", paddingBottom: "var(--space-2)" }}>
              <div>
                <strong>{t.name}</strong>
                <div style={{ color: "var(--color-text-muted)", fontSize: "var(--font-size-sm)" }}>{t.task_type} · {t.default_priority}</div>
              </div>
              <Button size="sm" variant="danger" onClick={() => removeTemplate(t.template_id)}>Remove</Button>
            </div>
          ))}
          <h3>New Template</h3>
          {/* 200, matching app.TaskTemplates.name and
              TaskTemplateCreateRequest. The API bound was already right;
              nothing on screen said so, which is the
              bound-stated-in-one-layer failure this repo keeps recording
              -- a limit you meet by being refused. */}
          <input id="team-tasks-page-name" name="name" placeholder="Template name (e.g. Spring Inspection)" maxLength={200}
                 value={templateForm.name}
                 onChange={(e) => setTemplateForm((f) => ({ ...f, name: e.target.value }))}
                 className="touch-target" />
          {/* The SAME control the Assign Task modal uses, which this one
              had no reason to differ from. A free text box here invites a
              DESCRIPTION into a column the rest of the app treats as a
              CATEGORY -- the identical bug mobile had on TeamDashboard,
              where any report grouping by task type saw one-off strings
              beside real categories. A template is worse than a one-off
              task for this, because it stamps the typo onto every task
              made from it. */}
          <SelectOrAdd
            listKey="task_type"
            label="Task type"
            value={templateForm.task_type}
            onChange={(v) => setTemplateForm((f) => ({ ...f, task_type: v }))}
          />
          <select id="team-tasks-page-default-priority" name="default-priority" value={templateForm.default_priority} onChange={(e) => setTemplateForm((f) => ({ ...f, default_priority: e.target.value }))} className="touch-target">
            {["Low", "Medium", "High"].map((p) => <option key={p}>{p}</option>)}
          </select>
          {/* 1000, matching app.TaskTemplates.default_description.
              Reported: "It might be meaningful if the 'Description' field
              told the users the character limit" -- and a template is the
              worst place to hit one silently, because whatever it holds
              gets stamped onto every task made from it. */}
          <Field label="Default description"
                 hint="Optional. Up to 1000 characters — it is copied onto every task made from this template.">
            {(id, aria) => (
              <textarea id={id} {...aria} rows={2} maxLength={1000}
                        value={templateForm.default_description}
                        onChange={(e) => setTemplateForm((f) => ({ ...f, default_description: e.target.value }))} />
            )}
          </Field>
          <Button onClick={createTemplate}>Save Template</Button>
        </div>
      </Modal>

      <Modal
        open={!!completingHandoff}
        onClose={() => setCompletingHandoff(null)}
        title="Complete Handoff"
        footer={<Button onClick={submitCompleteHandoff}>Mark Complete</Button>}
      >
        <div style={{ display: "grid", gap: "var(--space-3)" }}>
          <p style={{ color: "var(--color-text-muted)", fontSize: "var(--font-size-sm)" }}>
            Inspection #{completingHandoff?.inspection_id} handed off by user {completingHandoff?.from_user_id}.
            Only the recipient of a handoff can mark it complete.
          </p>
          <textarea id="team-tasks-page-complete-notes" name="complete-notes"
            placeholder="Completion notes (optional)"
            rows={3}
            value={completeNotes}
            onChange={(e) => setCompleteNotes(e.target.value)}
          />
        </div>
      </Modal>

      {/* Deleting a task took ONE CLICK and no confirmation -- a danger
          button on every row with nothing between it and gone. #328 and
          UI_ACTIONS.md rule 4 both say a destructive action names the
          thing and says what cannot be undone; this one did neither
          because it had no dialog at all. The confirm label differs from
          the button that opened it, per the same rule. */}
      <ConfirmDialog
        open={Boolean(pendingDelete)}
        onClose={() => setPendingDelete(null)}
        onConfirm={confirmDeleteTask}
        title="Delete this task?"
        confirmLabel="Delete task"
        danger
      >
        {pendingDelete ? (
          <p>
            Deleting the <strong>{pendingDelete.task_type}</strong> task
            {pendingDelete.due_date ? <> due {formatDateOnly(pendingDelete.due_date)}</> : null}
            {pendingDelete.description ? <> — “{pendingDelete.description}”</> : null}.
            {" "}Whoever it is assigned to will no longer see it. There is no undo.
          </p>
        ) : null}
      </ConfirmDialog>
    </div>
  );
}
